// Rechner: Gardinenstange ausrichten (PRD §7.7). Interaktive Drag-Vorschau.
import React, { useEffect, useMemo, useState } from "react";
import { calcCurtain, fmtCm } from "@/lib/calc";
import { track, trackSettled } from "@/lib/track";
import EditorShell from "./EditorShell";
import { startDrag, round1, clamp, fmt, niceStep, niceCeil } from "@/components/preview/useDragSvg";
import { C, Svg, handle, cross, hDim, vDim } from "@/components/preview/canvasPrimitives";

const DEFAULTS = {
  windowWidth: "120",
  windowHeight: "140",
  overhang: "15",
  bracketFromEdge: "8",
  heightAboveWindow: "12",
  wallWidth: "",
};

const W = 880;
const H = 470;
const padL = 70;
const padR = 836;

export default function CurtainCalculator() {
  const [v, setV] = useState(DEFAULTS);
  const set = (k) => (val) => setV((s) => ({ ...s, [k]: val }));
  const setNow = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const num = (k) => {
    const x = parseFloat(String(v[k]).replace(",", "."));
    return isNaN(x) ? NaN : x;
  };

  const r = useMemo(() => calcCurtain(v), [v]);

  useEffect(() => {
    if (!r.ok) return;
    trackSettled("calculator_used", { calculator: "curtain", mode: "default" });
    trackSettled("result_shown", { calculator: "curtain", verdict: (r.warnings && r.warnings.length) ? "warn" : "ok" });
  }, [r]);

  const canvas = renderCanvas(v, r, num, setNow);
  const copyText = r.ok ? buildCopyText(r) : "";

  const fields = [
    { id: "windowWidth", label: "Fensterbreite", value: v.windowWidth, step: "1", onChange: set("windowWidth") },
    { id: "windowHeight", label: "Fensterhöhe", value: v.windowHeight, step: "1", onChange: set("windowHeight") },
    { id: "overhang", label: "Überstand je Seite", value: v.overhang, step: "0.5", onChange: set("overhang") },
    { id: "bracketFromEdge", label: "Halterabstand von Fensterkante", value: v.bracketFromEdge, step: "0.5", onChange: set("bracketFromEdge") },
    { id: "heightAboveWindow", label: "Höhe über Fenster", value: v.heightAboveWindow, step: "0.5", onChange: set("heightAboveWindow") },
    { id: "wallWidth", label: "Wandbreite (optional)", value: v.wallWidth, step: "1", onChange: set("wallWidth"), hint: "Leer lassen, um nur am Fenster zu messen." },
  ];

  return (
    <EditorShell
      testId="calc-curtain"
      canvas={canvas}
      canvasReady={r.ok}
      emptyHint="Gib Fenstermaße, Überstand und Halterabstand ein, um die Vorschau zu sehen."
      toolbarHint="Ziehe das Stangenende (Überstand), die Halter und die Höhe über dem Fenster."
      onReset={() => setV(DEFAULTS)}
      fields={fields}
      warnings={r.ok ? r.warnings : []}
      hasResult={r.ok}
      results={r.ok ? buildResults(r) : []}
      steps={r.ok ? buildSteps(r) : []}
      copyText={copyText}
    />
  );
}

function renderCanvas(v, r, num, setNow) {
  const els = [];
  if (!r.ok) {
    els.push(
      <text key="x" x={W / 2} y={H / 2} fill={C.dim} fontSize={16} textAnchor="middle" fontFamily="Inter,sans-serif">
        Fenstermaße eingeben, um die Vorschau zu sehen.
      </text>
    );
    return (
      <Svg vbW={W} vbH={H} ariaLabel="Vorschau: Gardinenstange ausrichten">
        {els}
      </Svg>
    );
  }
  const wh = r.windowHeight != null ? r.windowHeight : 140;
  const drawW = padR - padL;
  const T = r.wall != null && isFinite(r.wall) && r.wall > 0 ? r.wall : r.rodLength + 40;
  const scaleX = drawW / T;
  const sx = (cm) => padL + cm * scaleX;
  const xToCm = (px) => (px - padL) / scaleX;
  const winLeftCm = r.wall != null ? r.windowLeft : (T - r.windowWidth) / 2;
  const wL = sx(winLeftCm);
  const wR = sx(winLeftCm + r.windowWidth);
  const baseY = H - 60;
  const topPad = 64;
  const availV = baseY - topPad;
  const refCm = Math.max(170, wh + r.heightAboveWindow + 24);
  const scaleYc = availV / refCm;
  const vy = (cm) => baseY - cm * scaleYc;
  const winBot = vy(0);
  const winTop = vy(wh);
  const rodY = vy(wh + r.heightAboveWindow);
  const gap = winTop - rodY;
  const blue = C.blue;

  if (r.wall != null) els.push(<rect key="wall" x={padL} y={40} width={drawW} height={H - 92} fill={C.wall} stroke={C.wallStroke} strokeWidth={1.5} rx={4} />);
  else els.push(<rect key="wallf" x={padL} y={40} width={drawW} height={H - 92} fill="hsl(36 25% 94% / 0.5)" rx={4} />);

  const stepX = niceStep(r.wall != null ? r.wall : r.rodLength);
  const ruleY = winBot + 14;
  if (r.wall != null) {
    for (let c = 0; c <= r.wall + 0.01; c += stepX) {
      const X = sx(c);
      els.push(<line key={"cbt" + c} x1={X} y1={ruleY} x2={X} y2={ruleY + 6} stroke={C.wallStroke} strokeWidth={1} />);
      els.push(
        <text key={"cbl" + c} x={X} y={ruleY + 20} fill={C.dim} fontSize={11} textAnchor="middle" fontFamily="Inter,sans-serif" style={{ fontVariantNumeric: "tabular-nums" }}>
          {fmt(c)}
        </text>
      );
    }
  } else {
    const o = winLeftCm - r.overhang;
    for (let L = 0; L <= r.rodLength + 0.01; L += stepX) {
      const X = sx(o + L);
      els.push(<line key={"cbt" + L} x1={X} y1={ruleY} x2={X} y2={ruleY + 6} stroke={C.wallStroke} strokeWidth={1} />);
      els.push(
        <text key={"cbl" + L} x={X} y={ruleY + 20} fill={C.dim} fontSize={11} textAnchor="middle" fontFamily="Inter,sans-serif" style={{ fontVariantNumeric: "tabular-nums" }}>
          {fmt(L)}
        </text>
      );
    }
  }
  const ruleMaxY = niceCeil(wh + r.heightAboveWindow + 8);
  const stepY = niceStep(ruleMaxY);
  for (let c = 0; c <= ruleMaxY + 0.01; c += stepY) {
    const Y = vy(c);
    els.push(<line key={"clt" + c} x1={padL - 6} y1={Y} x2={padL} y2={Y} stroke={C.wallStroke} strokeWidth={1} />);
    els.push(
      <text key={"cll" + c} x={padL - 9} y={Y + 4} fill={C.dim} fontSize={11} textAnchor="end" fontFamily="Inter,sans-serif" style={{ fontVariantNumeric: "tabular-nums" }}>
        {fmt(c)}
      </text>
    );
  }

  els.push(<rect key="win" x={wL} y={winTop} width={Math.max(4, wR - wL)} height={winBot - winTop} fill="hsl(205 78% 40% / 0.10)" stroke={blue} strokeWidth={2} rx={3} />);
  els.push(<line key="wpv" x1={(wL + wR) / 2} y1={winTop} x2={(wL + wR) / 2} y2={winBot} stroke="hsl(205 78% 40% / 0.4)" strokeWidth={1.5} style={{ pointerEvents: "none" }} />);
  els.push(<line key="wph" x1={wL} y1={(winTop + winBot) / 2} x2={wR} y2={(winTop + winBot) / 2} stroke="hsl(205 78% 40% / 0.4)" strokeWidth={1.5} style={{ pointerEvents: "none" }} />);
  els.push(<rect key="sill" x={wL - 8} y={winBot} width={wR - wL + 16} height={7} rx={2} fill="hsl(205 78% 40% / 0.18)" stroke="hsl(205 78% 40% / 0.5)" strokeWidth={1} style={{ pointerEvents: "none" }} />);

  const rodL = sx(winLeftCm - r.overhang);
  const rodR = sx(winLeftCm + r.windowWidth + r.overhang);
  els.push(<rect key="rod" x={rodL} y={rodY - 4} width={Math.max(2, rodR - rodL)} height={8} rx={4} fill="hsl(188 72% 28% / 0.35)" stroke={C.objectStroke} strokeWidth={1.6} style={{ pointerEvents: "none" }} />);
  els.push(<circle key="f1" cx={rodL} cy={rodY} r={6} fill="hsl(188 72% 28% / 0.5)" stroke={C.objectStroke} strokeWidth={1.4} style={{ pointerEvents: "none" }} />);
  els.push(<circle key="f2" cx={rodR} cy={rodY} r={6} fill="hsl(188 72% 28% / 0.5)" stroke={C.objectStroke} strokeWidth={1.4} style={{ pointerEvents: "none" }} />);

  const bLx = sx(winLeftCm - r.bracketFromEdge);
  const bRx = sx(winLeftCm + r.windowWidth + r.bracketFromEdge);
  [bLx, bRx].forEach((bxp, i) =>
    els.push(<rect key={"brk" + i} x={bxp - 3} y={rodY + 4} width={6} height={Math.max(4, gap - 6)} fill="hsl(188 72% 28% / 0.4)" stroke={C.objectStroke} strokeWidth={1.2} style={{ pointerEvents: "none" }} />)
  );

  els.push(hDim("rl", rodL, rodR, Math.max(34, rodY - 30), "Stange " + fmt(r.rodLength) + " cm", C.dim, W));
  if (r.overhang > 0) {
    els.push(hDim("ovl", rodL, wL, rodY - 16, fmt(r.overhang), C.primary, W));
    els.push(hDim("ovr", wR, rodR, rodY - 16, fmt(r.overhang), C.primary, W));
  }
  els.push(vDim("ha", rodY, winTop, wL - 22, fmt(r.heightAboveWindow), C.dim, W));

  els.push(cross("bc0", bLx, rodY, null));
  els.push(
    cross("bc1", bRx, rodY, (ev) =>
      startDrag(ev, W, H, (cur) => setNow("bracketFromEdge", String(round1(clamp(xToCm(cur.x) - (winLeftCm + r.windowWidth), 0, Math.max(0, r.overhang))))))
    )
  );

  els.push(
    handle("hov", rodR, rodY, "ew-resize", (ev) =>
      startDrag(ev, W, H, (cur) => setNow("overhang", String(round1(clamp(xToCm(cur.x) - (winLeftCm + r.windowWidth), 0, 100))))), "v")
  );
  els.push(
    handle("hww", wR, (winTop + winBot) / 2, "ew-resize", (ev) =>
      startDrag(ev, W, H, (cur) => setNow("windowWidth", String(round1(clamp(2 * (xToCm(cur.x) - T / 2), 20, 400))))), "v")
  );
  els.push(
    handle("hwh", (wL + wR) / 2, winTop, "ns-resize", (ev) =>
      startDrag(ev, W, H, (cur) => setNow("windowHeight", String(round1(clamp((baseY - cur.y) / scaleYc, 20, 250))))), "h")
  );
  els.push(
    handle("hha", (wL + wR) / 2, rodY, "ns-resize", (ev) =>
      startDrag(ev, W, H, (cur) => setNow("heightAboveWindow", String(round1(clamp((baseY - cur.y) / scaleYc - wh, 0, 50))))), "h")
  );

  return (
    <Svg vbW={W} vbH={H} ariaLabel="Vorschau: Gardinenstange ausrichten">
      {els}
    </Svg>
  );
}

function buildResults(r) {
  const rows = [{ label: "Gesamtlänge der Stange", value: fmtCm(r.rodLength) }];
  if (r.wall != null) {
    rows.push({ label: "Halter links (von Wandkante)", value: fmtCm(r.bracketLeftFromWall) });
    rows.push({ label: "Halter rechts (von Wandkante)", value: fmtCm(r.bracketRightFromWall) });
  } else {
    rows.push({ label: "Halter links (außerhalb der Fensterkante)", value: fmtCm(r.bracketFromEdgeOut) });
    rows.push({ label: "Halter rechts (außerhalb der Fensterkante)", value: fmtCm(r.bracketFromEdgeOut) });
  }
  rows.push({ label: "Überstand je Seite", value: fmtCm(r.overhang) });
  rows.push({ label: "Empfohlene Höhe über dem Fenster", value: fmtCm(r.heightAboveWindow) });
  return rows;
}

function buildSteps(r) {
  return [
    `Wähle eine Stange mit mindestens ${fmtCm(r.rodLength)} Länge (Fenster und zweimal Überstand).`,
    r.wall != null
      ? `Setze die Halter bei ${fmtCm(r.bracketLeftFromWall)} und ${fmtCm(r.bracketRightFromWall)} von der linken Wandkante.`
      : `Setze die Halter je ${fmtCm(r.bracketFromEdgeOut)} außerhalb der Fensterkanten.`,
    `Montiere ${fmtCm(r.heightAboveWindow)} über der Fensteroberkante und richte mit der Wasserwaage aus.`,
    "Achte vor dem Bohren auf Leitungen und die Wandbeschaffenheit.",
  ];
}

function buildCopyText(r) {
  const lines = ["Gardinenstange ausrichten", `Gesamtlänge: ${fmtCm(r.rodLength)}`];
  if (r.wall != null) {
    lines.push(`Halter links (von Wandkante): ${fmtCm(r.bracketLeftFromWall)}`);
    lines.push(`Halter rechts (von Wandkante): ${fmtCm(r.bracketRightFromWall)}`);
  } else {
    lines.push(`Halter je außerhalb der Fensterkante: ${fmtCm(r.bracketFromEdgeOut)}`);
  }
  lines.push(`Überstand je Seite: ${fmtCm(r.overhang)}`);
  lines.push(`Höhe über Fenster: ${fmtCm(r.heightAboveWindow)}`);
  return lines.join("\n");
}
